import { showError } from '@automator/builder/utils';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import recipe builder services
import { getItemsActions, getItemsTriggers } from '@automator/builder/services/item';

/**
 * Validates the recipe and optionally shows error messages if validation fails.
 *
 * @param {Boolean} [showErrors=true] - Whether to display error messages when validation fails.
 * @return {Object} Validation state including `isValid` (boolean) and `message` (string) if invalid.
 */
const getRecipeValidityState = ( showErrors = true ) => {
	const validation = validateRecipe();

	if ( ! validation.isValid && showErrors ) {
		showError( validation.message );
	}

	return validation;
};

/**
 * Validates the recipe by checking triggers, actions, and user selectors.
 *
 * @return {Object} Validation result with `isValid` (boolean) and `message` (string) if invalid.
 */
const validateRecipe = () => {
	// Check if the recipe has at least one trigger
	if ( ! hasTriggers() ) {
		return {
			isValid: false,
			message: escapeHTML( __( 'Add a trigger to activate this recipe', 'uncanny-automator' ) ),
		};
	}

	// Check if at least one trigger is live
	if ( ! triggersOneLive() ) {
		return {
			isValid: false,
			message: escapeHTML( __( 'Set a trigger to live to activate this recipe', 'uncanny-automator' ) ),
		};
	}

	// Validate the user selector
	if ( ! validUserSelector() ) {
		return {
			isValid: false,
			message: escapeHTML( __( 'Specify the user the actions will be run on', 'uncanny-automator' ) ),
		};
	}

	// Check if the recipe has at least one action
	if ( ! hasActions() ) {
		return {
			isValid: false,
			message: escapeHTML( __( 'Add an action to activate this recipe', 'uncanny-automator' ) ),
		};
	}

	// Check if at least one action is live
	if ( ! actionsOneLive() ) {
		return {
			isValid: false,
			message: escapeHTML( __( 'Set an action to live to activate this recipe', 'uncanny-automator' ) ),
		};
	}

	return { isValid: true };
};

/**
 * Checks if the recipe has at least one trigger.
 *
 * @return {Boolean} True if triggers exist, otherwise false.
 */
const hasTriggers = () => {
	// Retrieve the list of triggers
	return getItemsTriggers().length > 0;
};

/**
 * Checks if at least one trigger is live.
 *
 * @return {Boolean} True if a live trigger exists, otherwise false.
 */
const triggersOneLive = () => {
	// Check if at least one trigger is live
	return getItemsTriggers().some( trigger => trigger.is_item_on );
};

/**
 * Validates the user selector to ensure it's correctly set up for the recipe.
 *
 * @deprecated This method uses the old `.recipe` object and will be removed in future updates.
 * @return {Boolean} True if the user selector is valid, otherwise false.
 */
const validUserSelector = () => {
	// Check if the recipe type is 'user', where the user selector is not required
	if ( UncannyAutomator._recipe.recipe_type === 'user' ) {
		return true;
	}

	// Check if the recipe does not require user data
	if ( ! UncannyAutomator.recipe.requiresUserData ) {
		return true;
	}

	// Validate the user selector directly
	return UncannyAutomator.recipe.userSelector.isValid;
};

/**
 * Checks if the recipe has at least one action.
 *
 * @return {Boolean} True if actions exist, otherwise false.
 */
const hasActions = () => {
	// Retrieve the list of actions
	return getItemsActions().length > 0;
};

/**
 * Checks if at least one action is live.
 *
 * @return {Boolean} True if a live action exists, otherwise false.
 */
const actionsOneLive = () => {
	// Check if at least one action is live
	return getItemsActions().some( action => action.is_item_on );
};

export default getRecipeValidityState;
